import { useEffect } from 'react'
import { Link as InertiaLink, router, usePoll } from '@inertiajs/react'
import dayjs from 'dayjs'

import { Alert, Button, Link } from '@atoms'
import { Progress } from '@components/atoms/Progress'
import { FAILURES_ID, inlineLinkClasses } from '@components/imports/ImportSummary'
import { ImportStatusBadge } from '@components/imports/ImportStatusBadge'
import EmptyState from '@components/molecules/EmptyState'
import Pagination from '@components/molecules/Pagination'
import { SelectField } from '@components/molecules/SelectField'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { type Import, ImportType, ImportStatus, isSettled } from '@utils/imports/import'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('imports.history')
const tStatus = scopedTranslation('imports.statuses')
const tType = scopedTranslation('imports.types')

export type ImportHistoryFilters = { importType: ImportType | null; status: ImportStatus | null }

export type ImportHistoryProps = {
  imports: Import[]
  totalPages: number
  matchingCount: number
  totalCount: number
  filters: ImportHistoryFilters
  statuses: ImportStatus[]
  importTypes: ImportType[]
  importsPath: string
  newImportPath: string | null
  canDownload: boolean
}

const POLL_INTERVAL_MS = 5000
const RELOADED_PROPS = ['imports', 'totalPages', 'matchingCount', 'totalCount', 'filters']

function applyFilters(importsPath: string, filters: ImportHistoryFilters) {
  const query = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== null))
  router.get(importsPath, query, { only: RELOADED_PROPS, preserveState: true, preserveScroll: true, replace: true })
}

function FilterBar({
  importsPath,
  filters,
  statuses,
  importTypes,
}: Pick<ImportHistoryProps, 'importsPath' | 'filters' | 'statuses' | 'importTypes'>) {
  const update = (change: Partial<ImportHistoryFilters>) => applyFilters(importsPath, { ...filters, ...change })

  return (
    <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-3">
      {importTypes.length > 1 && (
        <div className="tw:w-full tw:sm:w-44">
          <SelectField<ImportType>
            hideLabel
            name="importType"
            label={t('filters.type')}
            emptyOptionLabel={t('filters.all_types')}
            options={importTypes.map((value) => ({ value, label: tType(value) }))}
            value={filters.importType}
            onValueChange={(importType) => update({ importType })}
          />
        </div>
      )}
      <div className="tw:w-full tw:sm:w-64">
        <SelectField<ImportStatus>
          hideLabel
          name="status"
          label={t('filters.status')}
          emptyOptionLabel={t('filters.all_statuses')}
          options={statuses.map((value) => ({ value, label: tStatus(value) }))}
          value={filters.status}
          onValueChange={(status) => update({ status })}
        />
      </div>
    </div>
  )
}

// error-600 and warning-700 are the lightest shades that pass 4.5:1 on white.
const TONE_CLASSES = { error: 'tw:text-error-600', warning: 'tw:text-warning-700' } as const

function ResultCount({
  value,
  label,
  tone,
  href,
}: {
  value: number
  label: string
  tone?: 'error' | 'warning'
  href?: string
}) {
  const toneClass = tone ? TONE_CLASSES[tone] : 'tw:text-gray-cool-900'

  return (
    <div className="tw:flex tw:items-baseline tw:gap-1">
      <dd className={cn('tw:m-0 tw:font-medium', value === 0 ? 'tw:text-gray-cool-400' : toneClass)}>
        {href && value > 0 ? (
          <InertiaLink
            href={href}
            className={cn(inlineLinkClasses, toneClass)}
            aria-label={t('failures_link_label', { count: value, label })}
          >
            {value.toLocaleString()}
          </InertiaLink>
        ) : (
          value.toLocaleString()
        )}
      </dd>
      <dt className={value === 0 ? 'tw:text-gray-cool-400' : 'tw:text-gray-cool-600'}>{label}</dt>
    </div>
  )
}

function ResultsCell({ import: item }: { import: Import }) {
  if (!isSettled(item.status) && item.submittedRowCount) {
    const percent = Math.round((item.processedRowCount / item.submittedRowCount) * 100)

    return (
      <div className="tw:flex tw:w-48 tw:flex-col tw:gap-1">
        <Progress size="compact" value={percent} aria-label={t('progress_label', { id: item.id })} />
        <span className="tw:text-xs tw:tabular-nums tw:text-gray-cool-600">
          {item.status === ImportStatus.Queued
            ? t('rows_waiting', { count: item.submittedRowCount })
            : t('rows_progress', {
                count: item.submittedRowCount,
                processed: item.processedRowCount.toLocaleString(),
                total: item.submittedRowCount.toLocaleString(),
              })}
        </span>
      </div>
    )
  }

  if (!item.counts) return <span className="tw:text-xs tw:text-gray-cool-500">{t('no_results')}</span>

  return (
    <dl className="tw:m-0 tw:flex tw:flex-wrap tw:gap-x-3 tw:gap-y-0.5 tw:text-xs tw:tabular-nums">
      <ResultCount value={item.counts.created} label={t('results.created')} />
      <ResultCount value={item.counts.updated} label={t('results.updated')} />
      <ResultCount
        value={item.counts.failed}
        label={t('results.failed')}
        tone="error"
        href={`${item.summaryPath}#${FAILURES_ID}`}
      />
      <ResultCount
        value={item.counts.skipped}
        label={t('results.skipped')}
        tone="warning"
        href={`${item.summaryPath}#${FAILURES_ID}`}
      />
    </dl>
  )
}

function FileCell({ import: item, canDownload }: { import: Import; canDownload: boolean }) {
  return (
    <span className="tw:flex tw:flex-col tw:gap-0.5">
      <span className="tw:max-w-56 tw:truncate tw:font-medium tw:text-gray-cool-900">
        {item.fileName ?? t('unnamed_file')}
      </span>
      {canDownload && item.fileHref && (
        <a
          href={item.fileHref}
          className={cn(inlineLinkClasses, 'tw:flex tw:w-fit tw:items-center tw:gap-1 tw:text-xs')}
          aria-label={t('download_label', { id: item.id })}
        >
          <Icon type="file-download-03" className="tw:h-3.5 tw:w-3.5" aria-hidden />
          {t('rows_as_imported')}
        </a>
      )}
    </span>
  )
}

function SubmittedCell({ import: item }: { import: Import }) {
  const submittedAt = dayjs(item.submittedAt)

  return (
    <span className="tw:flex tw:flex-col tw:gap-0.5">
      <span className="tw:text-gray-cool-900">{submittedAt.format('D MMM YYYY')}</span>
      <span className="tw:text-xs tw:text-gray-cool-600">
        {item.importer
          ? t('submitted_by', { time: submittedAt.format('h:mm a'), name: item.importer })
          : submittedAt.format('h:mm a')}
      </span>
    </span>
  )
}

const COLUMNS = ['type', 'file', 'status', 'submitted', 'results'] as const

function ImportsTable({ imports, canDownload }: { imports: Import[]; canDownload: boolean }) {
  return (
    <div className="tw:overflow-x-auto tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
      <table className="tw:min-w-full tw:border-collapse tw:text-sm">
        <caption className="tw:sr-only">{t('caption')}</caption>
        <thead>
          <tr className="tw:bg-gray-cool-50 tw:text-left tw:text-xs tw:text-gray-cool-600">
            {COLUMNS.map((column, index) => (
              <th
                key={column}
                scope="col"
                className={cn(
                  'tw:whitespace-nowrap tw:border-b tw:border-gray-200 tw:py-2.5 tw:font-medium',
                  index === 0 ? 'tw:px-5' : 'tw:px-3'
                )}
              >
                {t(`columns.${column}`)}
              </th>
            ))}
            <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-5 tw:py-2.5 tw:text-right">
              <span className="tw:sr-only">{t('summary_column')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {imports.map((item) => (
            <tr
              key={item.id}
              className="tw:border-b tw:border-gray-100 tw:last:border-0 tw:odd:bg-white tw:even:bg-gray-cool-50/40"
            >
              <td className="tw:whitespace-nowrap tw:px-5 tw:py-3 tw:align-top tw:font-medium tw:text-gray-cool-900">
                {tType(item.importType)}
              </td>
              <td className="tw:px-3 tw:py-3 tw:align-top">
                <FileCell import={item} canDownload={canDownload} />
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3 tw:align-top">
                <ImportStatusBadge status={item.status} />
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3 tw:align-top">
                <SubmittedCell import={item} />
              </td>
              <td className="tw:px-3 tw:py-3 tw:align-top">
                <ResultsCell import={item} />
              </td>
              <td className="tw:whitespace-nowrap tw:px-5 tw:py-3 tw:text-right tw:align-top">
                <Link
                  href={item.summaryPath}
                  as="inertiaLink"
                  rank="secondary"
                  size="sm"
                  ariaLabel={t('view_label', { id: item.id })}
                >
                  {t('view')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function NewImportLink({ href }: { href: string }) {
  return (
    <Link href={href} as="inertiaLink" rank="primary" leadingIcon="plus-circle">
      {t('new_import')}
    </Link>
  )
}

export function ImportHistory({
  imports,
  totalPages,
  matchingCount,
  totalCount,
  filters,
  statuses,
  importTypes,
  importsPath,
  newImportPath,
  canDownload,
}: ImportHistoryProps) {
  const filtering = Object.values(filters).some((value) => value !== null)
  const runningCount = imports.filter((item) => !isSettled(item.status)).length
  const clearFilters = () =>
    applyFilters(
      importsPath,
      Object.fromEntries(Object.keys(filters).map((key) => [key, null])) as ImportHistoryFilters
    )

  const { start, stop } = usePoll(POLL_INTERVAL_MS, { only: RELOADED_PROPS }, { autoStart: false })
  useEffect(() => {
    if (runningCount > 0) start()
    else stop()
  }, [runningCount, start, stop])

  return (
    <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-4">
      {totalCount > 0 && (
        <FilterBar importsPath={importsPath} filters={filters} statuses={statuses} importTypes={importTypes} />
      )}

      <div role="status">
        {filtering && matchingCount > 0 && (
          <Alert type="info" size="sm" icon="search-lg">
            <span className="tw:text-sm">
              {t('showing', {
                count: totalCount,
                matching: matchingCount.toLocaleString(),
                total: totalCount.toLocaleString(),
              })}{' '}
              <button type="button" onClick={clearFilters} className={inlineLinkClasses}>
                {t('clear_filters')}
              </button>
            </span>
          </Alert>
        )}
      </div>

      {imports.length === 0 ? (
        <EmptyState
          iconType="upload-cloud-02"
          headline={t(filtering ? 'no_matches.headline' : 'empty.headline')}
          subtext={t(filtering ? 'no_matches.subtext' : 'empty.subtext')}
        >
          {filtering ? (
            <Button rank="secondary" onClick={clearFilters}>
              {t('clear_filters')}
            </Button>
          ) : (
            newImportPath && <NewImportLink href={newImportPath} />
          )}
        </EmptyState>
      ) : (
        <ImportsTable imports={imports} canDownload={canDownload} />
      )}

      {totalPages > 1 && <Pagination totalPages={totalPages} only={RELOADED_PROPS} />}

      {runningCount > 0 && (
        <p className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-gray-cool-600">
          <Icon type="refresh" className="tw:h-3.5 tw:w-3.5" aria-hidden />
          {t('still_running', { count: runningCount })}
        </p>
      )}
    </div>
  )
}
